import {useMemo} from 'react'
import {useCurrentBasket} from './use-current-basket'
import {usePromotions} from '@salesforce/commerce-sdk-react'

/**
 * Extracts promotion identifiers associated with the passed product.
 * @param {array} bonusDiscountLineItems is the basket with all promotions
 * @param {object} bonusPromotion is the bonus promotion that should be rendered separately
 * @param {object} product is the product where the promotions should be applied
 * @param {string} variant is the variant of the product
 * @return array of promotion identifiers
 */
const getPromotionIds = function (bonusDiscountLineItems, bonusPromotion, product, variant) {
    if (!bonusPromotion) {
        return product?.productPromotions
            ?.map(({promotionId}) => promotionId)
            ?.filter((promotionId) => {
                if (variant === 'bonus') {
                    // we should restrict filtering by bonus promotions
                    if (!bonusDiscountLineItems) {
                        return false
                    }
                    return (
                        bonusDiscountLineItems.filter((bonus) => bonus.promotionId === promotionId)
                            .length !== 0
                    )
                }

                return true
            })
    }
    return [bonusPromotion.promotionId]
}

/**
 * Handles fetching product promotions.
 * @param {object} product - The product
 * @param {object} bonusPromotion - the bonus promotion
 * @param {string} variant - The variant of the product
 * @returns - Product details and promotions
 */
const useProductSpecialOffers = (product, bonusPromotion, variant) => {
    const {data: basket} = useCurrentBasket()

    const promotionIds = useMemo(() => {
        const ids = getPromotionIds(
            basket?.bonusDiscountLineItems,
            bonusPromotion,
            product,
            variant
        )
        return ids
    }, [product, bonusPromotion, basket?.bonusDiscountLineItems, variant])

    const {data, isInitialLoading: loading} = usePromotions(
        {
            parameters: {
                ids: promotionIds?.join(',')
            }
        },
        {
            enabled: promotionIds?.length > 0
        }
    )

    const promoCalloutMsgs = useMemo(() => {
        if (!product?.c_setPromoCalloutMsgs) return []
        try {
            return JSON.parse(product.c_setPromoCalloutMsgs).map(
                ({promotion_id, callout_msg, badge, colorWorld, promotionIcon, details}) => ({
                    id: promotion_id,
                    calloutMsg: callout_msg,
                    c_badge: badge,
                    c_colorWorld: colorWorld,
                    c_promotionIcon: promotionIcon,
                    details: details
                })
            )
        } catch {
            return []
        }
    }, [product?.c_setPromoCalloutMsgs])

    const promotions = useMemo(() => {
        if (!promotionIds) {
            return []
        }
        const promotionResults = (data?.data || []).filter(({id}) => promotionIds.includes(id))
        const fetchedIds = new Set(promotionResults.map(({id}) => id))
        const merged = [
            ...promotionResults,
            ...promoCalloutMsgs.filter(({id}) => !fetchedIds.has(id))
        ]
        if (variant === 'bonus') {
            return merged
                .filter(({c_discountProducts}) => c_discountProducts)
                .map((promo) => ({
                    ...promo,
                    productItem: bonusPromotion
                        ? basket?.productItems.filter(
                              (item) => item?.bonusDiscountLineItemId === bonusPromotion.id
                          )
                        : []
                }))
        } else if (variant === 'pdp' || variant === 'xpdp') {
            return merged.filter(({calloutMsg}) => calloutMsg?.trim())
        } else {
            return merged
        }
    }, [promotionIds, data?.data, promoCalloutMsgs, variant, bonusPromotion, basket?.productItems])

    return {product, variant, promotions, loading}
}

export default useProductSpecialOffers
